// The onboarding (/welcome), once the address is confirmed: five quick questions, why Printags fits the answers, then
// the plan that opens the dashboard.
// No Nuxt dependency here: the file runs with Node alone. Its texts live in lang/*.json, under `welcome`, keyed by the
// codes below; they only promise what the sandbox, the dashboard and the plans do today (no invented price, delay or
// feature).
import { STEPS } from "./onboarding.js";

// The stepper (`welcome.phases.<key>`). "account" is already behind the person when the page opens (created, then
// confirmed): starting with one phase done says the hardest part is over.
export const WELCOME_PHASES = [{ key: "account" }, { key: "you" }, { key: "project" }, { key: "plan" }];

// The screens, in order. A single choice screen moves on by itself once picked with the pointer.
export const WELCOME_SCREENS = [
  { key: "role", phase: "you" },
  { key: "team", phase: "you" },
  { key: "goal", phase: "project" },
  { key: "volume", phase: "project" },
  { key: "website", phase: "project" },
  { key: "pitch", phase: "plan" },
  { key: "plan", phase: "plan" },
  { key: "billing", phase: "plan" }
];

// The codes are the API's (DISCOVERY_ROLES, DISCOVERY_TEAMS, DISCOVERY_GOALS, DISCOVERY_VOLUMES): sales automations read
// them as they are. Labels (`welcome.roles.<key>.label` and `.hint`, …) speak in the first person, which French keeps
// neutral. `hint: false`: the option has no hint.
export const WELCOME_ROLES = [
  { key: "developer", icon: "i-heroicons-code-bracket" },
  { key: "tech_lead", icon: "i-heroicons-cpu-chip" },
  { key: "product", icon: "i-heroicons-map" },
  { key: "founder", icon: "i-heroicons-building-office-2" },
  { key: "freelance", icon: "i-heroicons-briefcase" },
  { key: "other", icon: "i-heroicons-ellipsis-horizontal", hint: false }
];

export const WELCOME_TEAMS = [
  { key: "engineering", icon: "i-heroicons-code-bracket-square" },
  { key: "product", icon: "i-heroicons-squares-2x2" },
  { key: "marketing", icon: "i-heroicons-megaphone" },
  { key: "sales", icon: "i-heroicons-chat-bubble-left-right" },
  { key: "operations", icon: "i-heroicons-truck" },
  { key: "management", icon: "i-heroicons-flag" },
  { key: "other", icon: "i-heroicons-ellipsis-horizontal" }
];

// The team most people in a role belong to: pre-selected, one click to confirm or change.
const TEAM_OF_ROLE = { developer: "engineering", tech_lead: "engineering", product: "product", founder: "management" };
export const teamForRole = (role) => TEAM_OF_ROLE[role] ?? null;

export const WELCOME_GOALS = [
  { key: "platform", icon: "i-heroicons-code-bracket" },
  { key: "store", icon: "i-heroicons-shopping-bag" },
  { key: "campaign", icon: "i-heroicons-paper-airplane" },
  { key: "reseller", icon: "i-heroicons-building-storefront" },
  { key: "exploring", icon: "i-heroicons-sparkles" }
];

export const WELCOME_VOLUMES = [{ key: "small" }, { key: "medium" }, { key: "large" }, { key: "unknown" }];

const known = (options, key) => options.some((option) => option.key === key);

// Same caps and rule as the API: an event property at Mon Agence holds at most 500 characters.
export const WELCOME_MESSAGE_MAX = 500;
export const WELCOME_WEBSITE_MAX = 200;
const WEBSITE_RE = /^(https?:\/\/)?[a-z0-9][a-z0-9-]*(\.[a-z0-9-]+)+(:\d+)?([/?#]\S*)?$/i;

// An empty site is allowed (optional field); otherwise a domain name, with or without a scheme.
export const isValidWebsite = (value) => {
  const text = (value ?? "").trim();
  return text === "" || (text.length <= WELCOME_WEBSITE_MAX && WEBSITE_RE.test(text));
};

// The site as people read it: no scheme, no trailing slash.
export const displayWebsite = (value) => (value ?? "").trim().replace(/^https?:\/\//i, "").replace(/\/+$/, "");

// Saved answers that let the onboarding go straight to the plan (volume and site are optional).
export const answered = (discovery) => !!(discovery?.role && discovery?.team && discovery?.goal);

// Where a returning visitor picks up: why Printags fits, then the plan, once the answers are saved; else the first screen
// without an answer. The site screen comes last because it saves everything: a draft is never taken for a saved answer.
export const resumeScreen = (saved, draft) => {
  if (answered(saved)) return "pitch";
  const answers = { ...(saved ?? {}), ...(draft ?? {}) };
  if (!answers.role) return "role";
  if (!answers.team) return "team";
  if (!answers.goal) return "goal";
  if (!answers.volume) return "volume";
  return "website";
};

// Someone who does not write the code is offered, in the dashboard, to invite the person who will.
const CODERS = ["developer", "tech_lead", "freelance"];
export const writesNoCode = (discovery) => !!discovery?.role && !CODERS.includes(discovery.role) && discovery.team !== "engineering";

// What each goal changes to the path (`welcome.paths.<key>.title`, `.lead`, `.note`): an unknown goal is discovering.
export const welcomePathKey = (goal) => (known(WELCOME_GOALS, goal) ? goal : "exploring");

// Why Printags fits the answers, before the plans: three points per goal (`welcome.pitches.<goal>.title`, its points'
// `.points.<key>.title` and `.text`), a line per volume (`welcome.volumeLines.<volume>`). Written by us, not generated:
// instant, read over, and it promises only what the API, the dashboard and the plans do today. The answers also reach
// the team (the CRM event of the API), which follows up by hand.
const PITCH_ICONS = {
  platform: ["i-heroicons-beaker", "i-heroicons-bolt", "i-heroicons-identification"],
  store: ["i-heroicons-arrow-path-rounded-square", "i-heroicons-truck", "i-heroicons-bell-alert"],
  campaign: ["i-heroicons-cursor-arrow-rays", "i-heroicons-identification", "i-heroicons-map-pin"],
  reseller: ["i-heroicons-building-storefront", "i-heroicons-tag", "i-heroicons-cpu-chip"],
  exploring: ["i-heroicons-beaker", "i-heroicons-squares-2x2", "i-heroicons-cursor-arrow-rays"]
};

// `answers`: { goal, volume, website }. The site, when given, names the project in the lead (`welcome.pitch.leadSite`).
export const welcomePitch = ({ goal, volume, website } = {}) => {
  const key = welcomePathKey(goal);
  return {
    key,
    site: displayWebsite(website),
    points: PITCH_ICONS[key].map((icon, index) => ({ icon, key: `p${ index + 1 }` })),
    volumeKey: known(WELCOME_VOLUMES, volume) ? volume : "unknown"
  };
};

// The first four steps of the dashboard's set-up, shown once the plan is active: the same steps, so the same titles and
// durations (`onboarding.steps.<key>`). Someone who does not write the code starts where the dashboard will start
// them: bringing in whoever will.
const ROADMAP_KEYS = ["testKey", "testOrder", "lifecycle", "webhookEndpoint"];
export const welcomeRoadmap = (discovery) => (writesNoCode(discovery) ? ["team", ...ROADMAP_KEYS.slice(0, 3)] : ROADMAP_KEYS)
  .filter((key) => STEPS.some((step) => step.key === key))
  .map((key) => ({ key }));

// ─── Plans ──────────────────────────────────────────────────────────────────

// Only this plan carries the free trial (controllers/transactions/subscriptions.js on the API side).
export const TRIAL_PLAN = "professional";
export const TRIAL_DAYS = 14;

// The plan names live in lang/*.json with an emoji ("Offre Classique 🚀"): the onboarding shows the words only.
export const plainName = (label) => String(label ?? "").replace(/[\p{Extended_Pictographic}️‍]/gu, "").replace(/\s+/g, " ").trim();

// The paid plans, cheapest first, the trial plan flagged when the account can still use it.
export const offerablePlans = (available, canUseTrial) => (available ?? [])
  .filter((plan) => Number(plan.price) > 0)
  .sort((a, b) => Number(a.price) - Number(b.price))
  .map((plan) => ({ ...plan, trial: !!canUseTrial && plan.displayname === TRIAL_PLAN }));

// Pre-selected: the trial plan when there is one (the commitment the onboarding ends on), else nothing.
export const defaultPlanId = (plans) => plans.find((plan) => plan.trial)?.id ?? null;

// ─── Draft ──────────────────────────────────────────────────────────────────

// Answers kept per account and per browser until they are saved: a closed tab or a reload resumes where it stopped.
const draftKey = (accountId) => `printags:welcome-draft:${ accountId }`;
const DRAFT_FIELDS = ["role", "team", "goal", "volume", "website", "message"];

export const loadDraft = (accountId) => {
  try {
    const value = JSON.parse(localStorage.getItem(draftKey(accountId)) ?? "null");
    if (!value || typeof value !== "object") return null;
    return Object.fromEntries(DRAFT_FIELDS.filter((field) => typeof value[field] === "string").map((field) => [field, value[field]]));
  } catch {
    return null;
  }
};

export const saveDraft = (accountId, answers) => {
  try {
    localStorage.setItem(draftKey(accountId), JSON.stringify(Object.fromEntries(DRAFT_FIELDS.map((field) => [field, answers[field] ?? null]))));
  } catch { /* private browsing: the answers simply live until the tab closes */ }
};

export const clearDraft = (accountId) => {
  try { localStorage.removeItem(draftKey(accountId)); } catch { /* nothing to clear */ }
};

// The plan chosen before leaving for Stripe, read back on return to word the confirmation (trial or not).
const CHECKOUT_KEY = "printags:welcome-checkout";
export const rememberCheckout = (plan) => {
  try { sessionStorage.setItem(CHECKOUT_KEY, JSON.stringify({ id: plan.id, name: plan.name, trial: !!plan.trial })); } catch { /* see below */ }
};
export const recallCheckout = () => {
  try { return JSON.parse(sessionStorage.getItem(CHECKOUT_KEY) ?? "null"); } catch { return null; }
};
export const forgetCheckout = () => {
  try { sessionStorage.removeItem(CHECKOUT_KEY); } catch { /* without storage the confirmation stays generic */ }
};

// Webmail shortcuts on the "check your inbox" screen, for the providers people use most: one click instead of a search.
// `name`: the webmail's own name, in « Ouvrir Gmail » (`welcome.webmail`).
const WEBMAILS = [
  { match: /^(gmail|googlemail)\.com$/i, name: "Gmail", url: "https://mail.google.com/mail/u/0/#search/from%3Aprintags.com" },
  { match: /^(outlook|hotmail|live|msn)\.[a-z.]+$/i, name: "Outlook", url: "https://outlook.live.com/mail/0/" },
  { match: /^(yahoo|ymail)\.[a-z.]+$/i, name: "Yahoo Mail", url: "https://mail.yahoo.com/" },
  { match: /^(orange|wanadoo)\.fr$/i, name: "Mail Orange", url: "https://mail.orange.fr/" },
  { match: /^(free)\.fr$/i, name: "Free Mail", url: "https://webmail.free.fr/" },
  { match: /^(icloud|me|mac)\.com$/i, name: "iCloud Mail", url: "https://www.icloud.com/mail" }
];

export const webmailFor = (email) => {
  const domain = String(email ?? "").split("@")[1] ?? "";
  const found = WEBMAILS.find((webmail) => webmail.match.test(domain));
  return found ? { name: found.name, url: found.url } : null;
};

// Seconds to wait before the link can be sent again: the API allows three per minute, the screen one.
export const RESEND_COOLDOWN_S = 60;
